import { SmartConnector } from '__pages/SmartConnectors/types';
import {
  StyledApiNameInput,
  StyledSettingsContentCard,
  StyledStatusInputControl,
  StyleRelativeWrapper,
} from '../styles';
import { SubrouteComponent } from '../types';
import SmartConnectorService from 'services/SmartConnectorService';
import { getErrorMessage } from '__hooks/useErrors';
import { useToast } from '__components/ToastProvider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toastVariant } from '__components/ToastProvider';
import { SubSectionWithHeader as SubSection } from 'components/Layout/SubSection';
import Cols from '__pages/Styleguide/components/helpers/Cols';
import InputControl from '__components/Inputs/InputControl';
import { getOriginalError } from '__services/AxiosService';
import { SmartConnectorNameInput } from '../../../components/SmartConnectorNameInput';
import { StatusText } from '__pages/SmartConnectors/components/StatusText';
import useField from '__hooks/useField';
import { EmailInput } from '../EmailInput';
import { MaxConcurrentExecutionsInput } from '../MaxConcurrentExecutionsInput';

export const Settings: SubrouteComponent = ({
  smartConnector,
  setSmartConnector,
  access,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const [errors, setErrors] = useState<Record<string, string>>({ name: '' });
  const [patching, setPatching] = useState(false);

  const [name, setName] = useField(smartConnector.name);
  const [errorEmail, setErrorEmail] = useField(
    smartConnector.error_notification_email
  );
  const [allEmail, setAllEmail] = useField(
    smartConnector.all_notification_email
  );
  const [maxConcurrentExecutions, setMaxConcurrentExecutions] = useField(
    smartConnector.max_concurrent_executions
  );

  const patchSmartConnector = async (payload: Partial<SmartConnector>) => {
    setPatching(true);
    try {
      const { data } = await SmartConnectorService.updateSmartConnector(
        smartConnector.id,
        payload
      );
      setSmartConnector(data);

      return true;
    } catch (err: any) {
      const orig = getOriginalError(err);
      if (
        orig?.name ||
        orig?.error_notification_email ||
        orig?.all_notification_email ||
        orig?.api_name ||
        orig?.max_concurrent_executions
      ) {
        setErrors((prev) => ({
          ...prev,
          name: orig.name || '',
          api_name: orig.api_name || '',
          error_notification_email: orig.error_notification_email || '',
          all_notification_email: orig.all_notification_email || '',
          max_concurrent_executions: orig.max_concurrent_executions || '',
        }));
      } else {
        showToast({
          message:
            getErrorMessage(orig) || t('Failed to update Smart Connector'),
          variant: toastVariant.FAILURE,
        });
      }
      return false;
    } finally {
      setPatching(false);
    }
  };

  const onChangeName = (value: string) => {
    setName(value);
    setErrors((prev) => ({ ...prev, name: '' }));
  };

  const patchName = async () => {
    const trimmed = name.trim();
    setName(trimmed);
    if (smartConnector.name !== trimmed) {
      patchSmartConnector({ name: trimmed });
    }
  };

  const onChangeErrorEmail = (value: string) => {
    setErrorEmail(value);
    setErrors((prev) => ({ ...prev, error_notification_email: '' }));
  };

  const onChangeAllEmail = (value: string) => {
    setAllEmail(value);
    setErrors((prev) => ({ ...prev, all_notification_email: '' }));
  };

  const patchErrorEmail = async () => {
    const trimmed = (errorEmail || '').trim();
    setErrorEmail(trimmed);
    if (smartConnector.error_notification_email !== trimmed) {
      patchSmartConnector({ error_notification_email: trimmed });
    }
  };

  const patchAllEmail = async () => {
    const trimmed = (allEmail || '').trim();
    setAllEmail(trimmed);
    if (smartConnector.all_notification_email !== trimmed) {
      patchSmartConnector({ all_notification_email: trimmed });
    }
  };

  const onChangeMaxConcurrentExecutions = (value: number) => {
    setMaxConcurrentExecutions(value);
    setErrors((prev) => ({ ...prev, max_concurrent_executions: '' }));
  };

  const patchMaxConcurrentExecutions = async () => {
    if (smartConnector.max_concurrent_executions !== maxConcurrentExecutions) {
      patchSmartConnector({
        max_concurrent_executions: maxConcurrentExecutions,
      });
    }
  };

  return (
    <StyledSettingsContentCard>
      <SubSection title={t('SmartConnector Settings')} action={null}>
        <Cols columns={3} gutter="20px">
          <InputControl margin>
            <SmartConnectorNameInput
              error={errors.name}
              value={name}
              onChange={onChangeName}
              data-qa="smartconnector-name-input"
              onBlur={patchName}
              disabled={!access.edit || patching}
            />
          </InputControl>
          <StyleRelativeWrapper>
            <StyledApiNameInput value={smartConnector.api_name} />
          </StyleRelativeWrapper>
          <StyleRelativeWrapper>
            <StyledStatusInputControl label={t('Status')}>
              <StatusText status={smartConnector.status} />
            </StyledStatusInputControl>
          </StyleRelativeWrapper>
        </Cols>
        <Cols columns={3} gutter="20px">
          <MaxConcurrentExecutionsInput
            value={maxConcurrentExecutions}
            onChange={onChangeMaxConcurrentExecutions}
            error={errors.max_concurrent_executions}
            onBlur={patchMaxConcurrentExecutions}
            disabled={!access.edit || patching}
          />
          <EmailInput
            value={allEmail}
            error={errors.all_notification_email}
            onChange={onChangeAllEmail}
            onBlur={patchAllEmail}
            disabled={!access.edit || patching}
            labelInfo={t(
              'Notification emails will always be sent to the uploading user by default, but you may also add another email recipient. This address will receive both Success and Error notifications.'
            )}
            label={t('Additional Notification Email')}
            margin
          />
          <EmailInput
            value={errorEmail}
            error={errors.error_notification_email}
            onChange={onChangeErrorEmail}
            onBlur={patchErrorEmail}
            disabled={!access.edit || patching}
            labelInfo={t(
              'Error emails will always be sent to the uploading user by default, but you may also add another email recipient (such as a distribution group).'
            )}
            label={t('Additional Error Notification Email')}
          />
        </Cols>
      </SubSection>
    </StyledSettingsContentCard>
  );
};
